import { Notification, NotificationProps, toaster } from 'rsuite';
import { isValidElement } from 'react';
import type { CSSProperties, ReactElement } from 'react';

type Status = NonNullable<NotificationProps['type']>;

const typeStyles: Record<Status, CSSProperties> = {
  success: { backgroundColor: '#edf7ed', borderColor: '#52c41a', color: '#237804' },
  error:   { backgroundColor: '#fff2f0', borderColor: '#ff4d4f', color: '#a8071a' },
  warning: { backgroundColor: '#fffbe6', borderColor: '#faad14', color: '#ad6800' },
  info:    { backgroundColor: '#e6f4ff', borderColor: '#1677ff', color: '#0958d9' },
};

let handledApiErrorUntil = 0;

const isCenteredPlacement = (placement?: string): boolean => {
  return !placement || placement === 'topCenter' || placement === 'bottomCenter';
};

const shouldSuppressCenteredError = (message: unknown, placement?: string): boolean => {
  if (Date.now() > handledApiErrorUntil || !isCenteredPlacement(placement) || !isValidElement(message)) {
    return false;
  }

  const notificationProps = (message.props ?? {}) as {
    'data-notification-type'?: string;
    'data-notification-message'?: string;
    children?: unknown;
  };
  const notificationType = notificationProps['data-notification-type'];
  const notificationMessage = notificationProps['data-notification-message'];
  const elementChildren = notificationProps.children;
  const rawMessage = typeof elementChildren === 'string'
    ? elementChildren
    : typeof notificationMessage === 'string'
      ? notificationMessage
      : undefined;

  const isGeneric422Message = typeof rawMessage === 'string'
    && rawMessage.startsWith('Generic Error: status: 422;');

  return isGeneric422Message || (
    notificationType === 'error'
    && notificationMessage === 'Ошибка'
  );
};

const originalToasterPush = toaster.push.bind(toaster);

toaster.push = ((message, options, userCreateRoot) => {
  if (shouldSuppressCenteredError(message, options?.placement)) {
    return Promise.resolve(undefined);
  }

  return originalToasterPush(message, options, userCreateRoot);
}) as typeof toaster.push;

export const markHandledApiErrorNotification = (): void => {
  handledApiErrorUntil = Date.now() + 1500;
};

const NotificationFactory = {
  create: (type: Status, message: string, closable = true): ReactElement => (
    <Notification
      type={type}
      closable={closable}
      style={typeStyles[type]}
      data-notification-type={type}
      data-notification-message={message}
    >
      <p style={{ margin: 0 }}>{message}</p>
    </Notification>
  ),

  success: (message = 'Успешно') =>
    NotificationFactory.create('success', message),

  error: (message = 'Ошибка') =>
    NotificationFactory.create('error', message),

  info: (message = 'Информация') =>
    NotificationFactory.create('info', message),

  warning: (message = 'Предупреждение') =>
    NotificationFactory.create('warning', message),

  successReboot: () =>
    NotificationFactory.create('success', 'Устройство перезапускается'),

  errorReboot: () =>
    NotificationFactory.create('error', 'Ошибка при перезапуске устройства'),
};

export default NotificationFactory;
